/**
 * A primary action with alternates one click away (Inbox "Create task", §0, §8.2).
 *
 * The inbox is a dumping ground for more than tasks — an idea is as often a new area, a
 * new project, or a note as it is something with a due date. The split button keeps the
 * fast path (title, click, done) as the default while putting the other three shapes a
 * single click behind the chevron, rather than four equally-weighted buttons competing for
 * the same row.
 */

import { useEffect, useRef, useState, type CSSProperties } from "react";
import { Chevron } from "./icons";

/** `anchor-name`/`position-anchor` aren't in `CSSProperties` yet (the types lag the spec) —
 * this just widens the inline `style` object enough to set them without an `any` cast. */
type AnchorStyle = CSSProperties & { anchorName?: string; positionAnchor?: string };

export type SplitButtonItem = {
  key: string;
  /** A string for the common case; a node (an icon plus text, e.g. the AI tier picker's
   * lightbulb) when a flyout item wants more than plain text. */
  label: React.ReactNode;
  onSelect: () => void;
  /** Shown but not clickable, with `title` explaining why (the AI tier picker's blocked
   * tiers, §11 item 9b) — listed rather than omitted, since "why isn't Bright here" is a
   * worse experience than "here's Bright, greyed out, and here's why." */
  disabled?: boolean;
  title?: string;
};

export function SplitButton({
  label,
  mainAriaLabel,
  onClick,
  items,
  disabled = false,
  mainDisabled = false,
  anchorName,
  className,
  toggleAriaLabel = "More ways to file this",
}: {
  /** A string for the common case; a node (an icon, e.g. the AI tier picker's lightbulb)
   * when the main face has no text of its own — pair with `mainAriaLabel` when it doesn't. */
  label: React.ReactNode;
  mainAriaLabel?: string;
  onClick: () => void;
  items: SplitButtonItem[];
  disabled?: boolean;
  /** Disables the main face alone, leaving the chevron live — for a main action that is
   * unavailable while the menu is how to make it available (the AI tier picker, off). */
  mainDisabled?: boolean;
  /** A CSS anchor name (`--foo`, unique per instance — see `AiBreakdownButton.tsx`) for the
   * flyout's `position-anchor`/CSS anchor positioning (§11 item 9b). Omitted by every caller
   * that doesn't use anchor positioning — the Inbox "Create task" button included, which
   * still positions its menu the plain relative-to-`.split-button` way. */
  anchorName?: string;
  /** Extra class on the root, for a caller that needs to find or restyle this one instance
   * (the Inbox row folds its "Create task" away while an AI retry is showing its answer).
   * Keep it constant between renders: a caller that toggles classes on the element itself
   * relies on React not rewriting the attribute. */
  className?: string;
  /** The chevron's accessible name. The default describes the Inbox's "Create task" menu; a
   * split button whose menu is something else (the AI tier) should say what it is, or a row
   * with two of them has two buttons by the same name that do different things. */
  toggleAriaLabel?: string;
}) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;

    // A menu that only closes on re-clicking its own toggle is a menu that stays open
    // while you go do something else on the page — closing on any outside interaction is
    // what makes it feel like a transient overlay rather than a stuck panel.
    function onPointerDown(event: PointerEvent) {
      if (rootRef.current && !rootRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    }
    function onKeyDown(event: KeyboardEvent) {
      if (event.key === "Escape") setOpen(false);
    }

    document.addEventListener("pointerdown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("pointerdown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open]);

  const anchorStyle: AnchorStyle | undefined = anchorName ? { anchorName } : undefined;
  const menuStyle: AnchorStyle | undefined = anchorName ? { positionAnchor: anchorName } : undefined;

  return (
    <div
      className={className ? `split-button ${className}` : "split-button"}
      ref={rootRef}
      style={anchorStyle}
    >
      <button
        type="button"
        className="btn small primary split-button-main"
        onClick={onClick}
        disabled={disabled || mainDisabled}
        aria-label={mainAriaLabel}
        title={mainAriaLabel}
      >
        {label}
      </button>
      <button
        type="button"
        className="btn small primary split-button-toggle"
        disabled={disabled}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={toggleAriaLabel}
        onClick={() => setOpen((current) => !current)}
      >
        <Chevron size={14} />
      </button>
      {open && (
        <ul className="split-button-menu" role="menu" style={menuStyle}>
          {items.map((item) => (
            <li key={item.key} role="none">
              <button
                type="button"
                role="menuitem"
                disabled={item.disabled}
                title={item.title}
                onClick={() => {
                  setOpen(false);
                  item.onSelect();
                }}
              >
                {item.label}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
